import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Avatar,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Divider
  ,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Schedule as TimeIcon } from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import DailyDetectionChart from '../components/DailyDetectionChart';
import DailyNewSpeciesChart from '../components/DailyNewSpeciesChart';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayChartOrder = [1, 2, 3, 4, 5, 6, 0];

const formatHourLabel = (hour) => {
  const period = hour >= 12 ? 'p' : 'a';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${period}`;
};

const formatHourRange = (startHour, hours = 2) => {
  const endHour = (startHour + hours) % 24;
  return `${formatHourLabel(startHour)}-${formatHourLabel(endHour)}`;
};

const parseDateBoundary = (dateStr, isEnd) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(
    year,
    month - 1,
    day,
    isEnd ? 23 : 0,
    isEnd ? 59 : 0,
    isEnd ? 59 : 0,
    isEnd ? 999 : 0
  );
};

const TimePatterns = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not configured.');
      }

      const pageSize = 1000;
      let from = 0;
      let allData = [];
      let hasMore = true;

      while (hasMore) {
        const { data, error: fetchError } = await supabase
          .from('detection')
          .select(`
            absolute_start_time,
            confidence,
            species (
              common_name
            )
          `)
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, from + pageSize - 1);

        if (fetchError) throw fetchError;

        allData = allData.concat(data || []);
        hasMore = data && data.length === pageSize;
        from += pageSize;
      }

      setRawData(allData);
    } catch (err) {
      console.error('Error fetching time pattern data:', err);
      setError(err.message);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (rawData.length === 0 || dateRange.start || dateRange.end) {
      return;
    }

    const dates = rawData
      .map((item) => item.absolute_start_time.split('T')[0])
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    if (dates.length > 0) {
      setDateRange({
        start: dates[0],
        end: dates[dates.length - 1]
      });
    }
  }, [rawData, dateRange.start, dateRange.end]);

  const filteredData = useMemo(() => {
    if (!dateRange.start || !dateRange.end) {
      return rawData;
    }

    const start = parseDateBoundary(dateRange.start, false);
    const end = parseDateBoundary(dateRange.end, true);
    if (!start || !end) {
      return rawData;
    }

    return rawData.filter((detection) => {
      const detectionDate = new Date(detection.absolute_start_time);
      return detectionDate >= start && detectionDate <= end;
    });
  }, [rawData, dateRange]);

  const {
    hourlyChartData,
    weeklyChartData,
    seasonalHourlyData,
    timeOfDayMixData,
    timeOfDayRadarData,
    timeOfDayBins,
    advancedMetrics,
    weekdayWeekendChartData,
    heatmapCounts,
    heatmapMax
  } = useMemo(() => {
    const hourlyCounts = Array(24).fill(0);
    const weekdayCounts = Array(7).fill(0);
    const weekdayDateSets = Array.from({ length: 7 }, () => new Set());
    const dailyCounts = {};
    const monthMap = new Map();
    const seasonalHourlyCounts = {
      Summer: Array(24).fill(0),
      Fall: Array(24).fill(0),
      Winter: Array(24).fill(0)
    };
    const seasonalDateSets = {
      Summer: new Set(),
      Fall: new Set(),
      Winter: new Set()
    };
    const timeOfDayBinsConfig = [
      { key: 'overnight', label: 'Overnight', start: 0, end: 4 },
      { key: 'dawn', label: 'Dawn', start: 5, end: 8 },
      { key: 'lateMorning', label: 'Late Morning', start: 9, end: 11 },
      { key: 'afternoon', label: 'Afternoon', start: 12, end: 15 },
      { key: 'dusk', label: 'Dusk', start: 16, end: 18 },
      { key: 'night', label: 'Night', start: 19, end: 23 }
    ];
    const heatmap = Array.from({ length: 7 }, () => Array(24).fill(0));
    const uniqueDates = new Set();

    filteredData.forEach((detection) => {
      const date = new Date(detection.absolute_start_time);
      if (Number.isNaN(date.getTime())) return;

      const hour = date.getHours();
      const weekday = date.getDay();
      const dateStr = detection.absolute_start_time.split('T')[0];

      hourlyCounts[hour] += 1;
      weekdayCounts[weekday] += 1;
      heatmap[weekday][hour] += 1;

      if (dateStr) {
        uniqueDates.add(dateStr);
        weekdayDateSets[weekday].add(dateStr);
        dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
      }

      const monthIndex = date.getMonth();
      let seasonLabel = null;
      if (monthIndex >= 5 && monthIndex <= 7) {
        seasonLabel = 'Summer';
      } else if (monthIndex >= 8 && monthIndex <= 10) {
        seasonLabel = 'Fall';
      } else {
        seasonLabel = 'Winter';
      }

      if (seasonalHourlyCounts[seasonLabel]) {
        seasonalHourlyCounts[seasonLabel][hour] += 1;
        if (dateStr) {
          seasonalDateSets[seasonLabel].add(dateStr);
        }
      }

      const monthKey = `${date.getFullYear()}-${String(monthIndex + 1).padStart(2, '0')}`;
      const currentMonth = monthMap.get(monthKey) || {
        detections: 0,
        species: new Set(),
        monthDate: new Date(date.getFullYear(), date.getMonth(), 1),
        timeOfDayCounts: timeOfDayBinsConfig.reduce((acc, bin) => {
          acc[bin.key] = 0;
          return acc;
        }, {})
      };
      currentMonth.detections += 1;
      const binMatch = timeOfDayBinsConfig.find((bin) => hour >= bin.start && hour <= bin.end);
      if (binMatch) {
        currentMonth.timeOfDayCounts[binMatch.key] += 1;
      }
      if (detection.species?.common_name) {
        currentMonth.species.add(detection.species.common_name);
      }
      monthMap.set(monthKey, currentMonth);
    });

    const totalDays = uniqueDates.size || 1;

    const hourlyData = hourlyCounts.map((count, hour) => ({
      hour,
      label: formatHourLabel(hour),
      totalDetections: count,
      avgDetections: Number((count / totalDays).toFixed(2))
    }));

    const weeklyData = weekdayChartOrder.map((weekdayIndex) => {
      const total = weekdayCounts[weekdayIndex];
      const dayCount = weekdayDateSets[weekdayIndex].size || 1;
      return {
        day: weekdayLabels[weekdayIndex],
        totalDetections: total,
        avgDetections: Number((total / dayCount).toFixed(2))
      };
    });

    const seasonalTimeOfDay = ['Summer', 'Fall', 'Winter'].map((season) => {
      const counts = timeOfDayBinsConfig.reduce((acc, bin) => {
        acc[bin.key] = 0;
        return acc;
      }, {});
      return { season, counts };
    });

    const seasonalTimeOfDayMap = seasonalTimeOfDay.reduce((acc, entry) => {
      acc[entry.season] = entry;
      return acc;
    }, {});

    Array.from(monthMap.values()).forEach((entry) => {
      const monthIndex = entry.monthDate.getMonth();
      let seasonLabel = null;
      if (monthIndex >= 5 && monthIndex <= 7) {
        seasonLabel = 'Summer';
      } else if (monthIndex >= 8 && monthIndex <= 10) {
        seasonLabel = 'Fall';
      } else {
        seasonLabel = 'Winter';
      }
      const target = seasonalTimeOfDayMap[seasonLabel];
      if (!target) return;
      timeOfDayBinsConfig.forEach((bin) => {
        target.counts[bin.key] += entry.timeOfDayCounts[bin.key] || 0;
      });
    });

    const timeOfDayMix = ['Summer', 'Fall', 'Winter'].map((season) => {
      const entry = seasonalTimeOfDayMap[season];
      const base = { season };
      timeOfDayBinsConfig.forEach((bin) => {
        base[bin.key] = entry?.counts[bin.key] || 0;
      });
      return base;
    });

    const timeOfDayRadar = timeOfDayBinsConfig.map((bin) => ({
      bin: bin.label,
      Summer: seasonalTimeOfDayMap.Summer?.counts[bin.key] || 0,
      Fall: seasonalTimeOfDayMap.Fall?.counts[bin.key] || 0,
      Winter: seasonalTimeOfDayMap.Winter?.counts[bin.key] || 0
    }));

    const dailyTotals = Object.values(dailyCounts);
    const meanDaily = dailyTotals.length > 0
      ? dailyTotals.reduce((sum, value) => sum + value, 0) / dailyTotals.length
      : 0;
    const variance = dailyTotals.length > 0
      ? dailyTotals.reduce((sum, value) => sum + Math.pow(value - meanDaily, 2), 0) / dailyTotals.length
      : 0;
    const stdDev = Math.sqrt(variance);
    const volatility = meanDaily > 0 ? (stdDev / meanDaily) * 100 : 0;

    let bestWindow = { hour: 0, total: 0 };
    hourlyCounts.forEach((count, hour) => {
      const nextHour = (hour + 1) % 24;
      const windowTotal = count + hourlyCounts[nextHour];
      if (windowTotal > bestWindow.total) {
        bestWindow = { hour, total: windowTotal };
      }
    });

    const mostActiveWeekday = weeklyData.reduce((top, current) => (
      current.totalDetections > top.totalDetections ? current : top
    ), weeklyData[0] || { day: 'N/A', totalDetections: 0 });

    const monthlyTotals = Array.from(monthMap.values())
      .map((entry) => ({
        month: entry.monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        detections: entry.detections
      }));
    const mostActiveMonth = monthlyTotals.reduce((top, current) => (
      current.detections > top.detections ? current : top
    ), monthlyTotals[0] || { month: 'N/A', detections: 0 });

    const weekdayTotal = weekdayCounts[1] + weekdayCounts[2] + weekdayCounts[3] + weekdayCounts[4] + weekdayCounts[5];
    const weekendTotal = weekdayCounts[0] + weekdayCounts[6];
    const weekdayDays = weekdayDateSets[1].size + weekdayDateSets[2].size + weekdayDateSets[3].size + weekdayDateSets[4].size + weekdayDateSets[5].size;
    const weekendDays = weekdayDateSets[0].size + weekdayDateSets[6].size;

    const weekdayWeekendData = [
      {
        label: 'Weekdays',
        avgDetections: Number(((weekdayTotal / (weekdayDays || 1)) || 0).toFixed(2)),
        totalDetections: weekdayTotal
      },
      {
        label: 'Weekend',
        avgDetections: Number(((weekendTotal / (weekendDays || 1)) || 0).toFixed(2)),
        totalDetections: weekendTotal
      }
    ];

    const heatmapPeak = heatmap.reduce((max, row) => {
      const rowMax = Math.max(...row);
      return Math.max(max, rowMax);
    }, 0);

    const seasonalHourly = Array.from({ length: 24 }, (_, hour) => {
      const entry = { hour, label: formatHourLabel(hour) };
      ['Summer', 'Fall', 'Winter'].forEach((season) => {
        const dayCount = seasonalDateSets[season].size || 1;
        entry[season] = Number((seasonalHourlyCounts[season][hour] / dayCount).toFixed(2));
      });
      return entry;
    });

    return {
      hourlyChartData: hourlyData,
      weeklyChartData: weeklyData,
      seasonalHourlyData: seasonalHourly,
      timeOfDayMixData: timeOfDayMix,
      timeOfDayRadarData: timeOfDayRadar,
      timeOfDayBins: timeOfDayBinsConfig,
      weekdayWeekendChartData: weekdayWeekendData,
      heatmapCounts: heatmap,
      heatmapMax: heatmapPeak,
      advancedMetrics: {
        peakWindow: formatHourRange(bestWindow.hour),
        mostActiveWeekday: mostActiveWeekday.day,
        mostActiveMonth: mostActiveMonth.month,
        volatility: `${volatility.toFixed(1)}%`
      }
    };
  }, [filteredData]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <TimeIcon />
          </Avatar>
          <Typography variant="h4" fontWeight="bold">
            ⏰ Time Patterns
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Temporal analysis of bird activity, seasonal patterns, and time-based behavioral insights.
        </Typography>
      </Box>

      <Box sx={{ px: 3 }}>
        <DailyDetectionChart />
        <DailyNewSpeciesChart />

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6">Time Patterns Filter</Typography>
                <Typography variant="body2" color="text.secondary">
                  Apply a single date range to daily, weekly, seasonal, and advanced analyses.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress />
              <Typography variant="h6">Loading time pattern data...</Typography>
            </Stack>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
            <Alert severity="error">Error loading time patterns: {error}</Alert>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 3
              }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📅 Daily Patterns
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Average detections per hour across the selected date range.
                    </Typography>
                    <Box sx={{ width: '100%', height: 320 }}>
                      {hourlyChartData.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="body2" color="text.secondary">
                            No detections in the selected range.
                          </Typography>
                        </Box>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={hourlyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="label"
                              angle={-45}
                              textAnchor="end"
                              height={50}
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              label={{ value: 'Avg detections', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip
                              formatter={(value, name, props) => {
                                if (name === 'avgDetections') {
                                  return [value, 'Avg detections'];
                                }
                                return [value, 'Total detections'];
                              }}
                              labelFormatter={(label) => `Hour: ${label}`}
                            />
                          <Bar dataKey="avgDetections" fill="#1976d2" radius={[4, 4, 0, 0]} barSize={18} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                  </CardContent>
                </Card>

              <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📆 Weekly Trends
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Average detections by day-of-week within the range.
                    </Typography>
                    <Box sx={{ width: '100%', height: 260 }}>
                      {weeklyChartData.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="body2" color="text.secondary">
                            No detections in the selected range.
                          </Typography>
                        </Box>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={weeklyChartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              label={{ value: 'Avg detections', angle: -90, position: 'insideLeft' }}
                            />
                            <Tooltip
                              formatter={(value, name) => {
                                if (name === 'avgDetections') {
                                  return [value, 'Avg detections'];
                                }
                                return [value, 'Total detections'];
                              }}
                            />
                          <Bar dataKey="avgDetections" fill="#2e7d32" radius={[4, 4, 0, 0]} barSize={26} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                  </CardContent>
                </Card>
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 3
              }}
            >
              <Card sx={{ height: '100%' }}>
                  <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🌍 Seasonal Activity Profile
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Average detections by hour for each season.
                  </Typography>
                  <Box sx={{ width: '100%', height: 320 }}>
                    {seasonalHourlyData.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography variant="body2" color="text.secondary">
                          No detections in the selected range.
                        </Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={seasonalHourlyData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="label"
                            angle={-45}
                            textAnchor="end"
                            height={50}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Avg detections', angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip />
                          <Legend verticalAlign="top" height={28} />
                          <Line type="monotone" dataKey="Summer" stroke="#ff8f00" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="Fall" stroke="#6d4c41" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="Winter" stroke="#1976d2" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                  </CardContent>
                </Card>

              <Card sx={{ height: '100%' }}>
                  <CardContent>
                  <Typography variant="h6" gutterBottom>
                    🌤️ Time-of-Day Mix by Season
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Radar view of detections across six time bins.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.6fr) minmax(360px, 1.2fr)' },
                      gap: 2,
                      alignItems: 'stretch'
                    }}
                  >
                    <Box sx={{ width: '100%', height: 320 }}>
                    {timeOfDayRadarData.length === 0 ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography variant="body2" color="text.secondary">
                          No detections in the selected range.
                        </Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={timeOfDayRadarData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="bin" tick={{ fontSize: 12 }} />
                          <PolarRadiusAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 12 }} />
                          <Radar name="Summer" dataKey="Summer" stroke="#ff8f00" fill="#ff8f00" fillOpacity={0.2} />
                          <Radar name="Fall" dataKey="Fall" stroke="#6d4c41" fill="#6d4c41" fillOpacity={0.2} />
                          <Radar name="Winter" dataKey="Winter" stroke="#1976d2" fill="#1976d2" fillOpacity={0.2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    )}
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ height: 320, overflowX: 'hidden' }}>
                      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ px: 1, py: 0.75, fontSize: 12 }}>Bin</TableCell>
                            <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>Summer</TableCell>
                            <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>Fall</TableCell>
                            <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>Winter</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {timeOfDayRadarData.map((row) => (
                            <TableRow key={row.bin}>
                              <TableCell sx={{ px: 1, py: 0.75, fontSize: 12 }}>{row.bin}</TableCell>
                              <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>{row.Summer}</TableCell>
                              <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>{row.Fall}</TableCell>
                              <TableCell align="right" sx={{ px: 1, py: 0.75, fontSize: 12 }}>{row.Winter}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  </CardContent>
                </Card>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ⏱️ Advanced Temporal Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Peak windows, volatility, and activity distribution across weeks and hours.
                  </Typography>

                    <Box
                      sx={{
                        mt: 2,
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(4, minmax(0, 1fr))'
                        },
                        gap: 2
                      }}
                    >
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="text.secondary">
                            Peak 2-hour Window
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {advancedMetrics.peakWindow}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="text.secondary">
                            Most Active Day
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {advancedMetrics.mostActiveWeekday}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="text.secondary">
                            Most Active Month
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {advancedMetrics.mostActiveMonth}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="caption" color="text.secondary">
                            Activity Volatility
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {advancedMetrics.volatility}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 2fr)' },
                      gap: 3
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                        Weekday vs Weekend Intensity
                      </Typography>
                      <Box sx={{ width: '100%', height: 320 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={weekdayWeekendChartData}
                              dataKey="avgDetections"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={120}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {weekdayWeekendChartData.map((entry, index) => (
                                <Cell key={entry.label} fill={['#1565c0', '#f57c00'][index % 2]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value, name, props) => {
                                const total = props?.payload?.totalDetections ?? 0;
                                return [`${value} avg (${total} total)`, name];
                              }}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                        Hour x Weekday Activity Heatmap
                      </Typography>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '80px repeat(24, 24px)',
                            gap: 0.75,
                            alignItems: 'center',
                            minWidth: 680
                          }}
                        >
                          <Box />
                          {Array.from({ length: 24 }).map((_, hour) => (
                            <Typography key={hour} variant="caption" color="text.secondary" align="center">
                              {hour % 6 === 0 ? formatHourLabel(hour) : ''}
                            </Typography>
                          ))}
                          {weekdayChartOrder.map((weekdayIndex) => (
                            <Box key={weekdayIndex} sx={{ display: 'contents' }}>
                              <Typography variant="caption" color="text.secondary">
                                {weekdayLabels[weekdayIndex]}
                              </Typography>
                              {heatmapCounts[weekdayIndex].map((value, hour) => {
                                const intensity = heatmapMax > 0 ? value / heatmapMax : 0;
                                return (
                                  <Box
                                    key={`${weekdayIndex}-${hour}`}
                                    title={`${weekdayLabels[weekdayIndex]} ${formatHourLabel(hour)}: ${value} detections`}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      bgcolor: `rgba(25, 118, 210, ${0.15 + intensity * 0.85})`,
                                      borderRadius: 0.5,
                                      border: '1px solid rgba(0,0,0,0.05)'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TimePatterns;
